import clsx from "clsx";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode
} from "react";

export interface PopupPoint {
  x: number;
  y: number;
}

export interface PopupSize {
  width: number;
  height: number;
}

interface DragState {
  pointerId: number;
  scaleX: number;
  scaleY: number;
  startClientX: number;
  startClientY: number;
  startX: number;
  startY: number;
}

export interface DraggablePopupProps {
  ariaLabel: string;
  bounds: PopupSize;
  children: ReactNode;
  className?: string;
  draggingClassName?: string;
  initialPosition: PopupPoint;
  margin?: number;
  onPositionChange?: (position: PopupPoint) => void;
  size: PopupSize;
  style?: CSSProperties;
}

export function DraggablePopup({
  ariaLabel,
  bounds,
  children,
  className,
  draggingClassName,
  initialPosition,
  margin = 0,
  onPositionChange,
  size,
  style
}: DraggablePopupProps) {
  const [position, setPosition] = useState(() => clampPopupPosition(initialPosition, size, bounds, margin));
  const [dragState, setDragState] = useState<DragState | null>(null);
  const onPositionChangeRef = useRef(onPositionChange);

  useEffect(() => {
    onPositionChangeRef.current = onPositionChange;
  }, [onPositionChange]);

  useEffect(() => {
    setPosition((current) => clampPopupPosition(current, size, bounds, margin));
  }, [bounds.height, bounds.width, margin, size.height, size.width]);

  useEffect(() => {
    if (!dragState) {
      return undefined;
    }

    const currentDragState = dragState;

    function handlePointerMove(event: PointerEvent) {
      if (event.pointerId !== currentDragState.pointerId) {
        return;
      }

      const nextPosition = clampPopupPosition({
        x: currentDragState.startX + (event.clientX - currentDragState.startClientX) / currentDragState.scaleX,
        y: currentDragState.startY + (event.clientY - currentDragState.startClientY) / currentDragState.scaleY
      }, size, bounds, margin);

      setPosition(nextPosition);
      onPositionChangeRef.current?.(nextPosition);
    }

    function handlePointerUp(event: PointerEvent) {
      if (event.pointerId === currentDragState.pointerId) {
        setDragState(null);
      }
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [bounds.height, bounds.width, dragState, margin, size.height, size.width]);

  function handlePointerDown(event: ReactPointerEvent<HTMLElement>) {
    if (isDragIgnored(event.target, event.currentTarget)) {
      return;
    }

    const coordinateSpace = event.currentTarget.offsetParent instanceof HTMLElement
      ? event.currentTarget.offsetParent
      : event.currentTarget.parentElement;
    const coordinateRect = coordinateSpace?.getBoundingClientRect();
    const scaleX = coordinateRect && coordinateRect.width > 0 ? coordinateRect.width / bounds.width : 1;
    const scaleY = coordinateRect && coordinateRect.height > 0 ? coordinateRect.height / bounds.height : 1;

    event.preventDefault();
    setDragState({
      pointerId: event.pointerId,
      scaleX: scaleX || 1,
      scaleY: scaleY || 1,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: position.x,
      startY: position.y
    });
  }

  return (
    <section
      className={clsx(
        "draggable-popup",
        className,
        dragState && "draggable-popup--dragging",
        dragState && draggingClassName
      )}
      aria-label={ariaLabel}
      data-dragging={dragState ? "true" : "false"}
      onPointerDown={handlePointerDown}
      style={{
        ...style,
        width: size.width,
        height: size.height,
        left: position.x,
        top: position.y
      }}
    >
      {children}
    </section>
  );
}

export function clampPopupPosition(
  position: PopupPoint,
  size: PopupSize,
  bounds: PopupSize,
  margin = 0
) {
  return {
    x: clamp(position.x, margin, Math.max(margin, bounds.width - size.width - margin)),
    y: clamp(position.y, margin, Math.max(margin, bounds.height - size.height - margin))
  };
}

function isDragIgnored(target: EventTarget, popup: HTMLElement) {
  if (!(target instanceof Element)) {
    return false;
  }

  const ignoredElement = target.closest(
    "button, a, input, select, textarea, [contenteditable='true'], [data-drag-ignore]"
  );
  return Boolean(ignoredElement && popup.contains(ignoredElement));
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
